import React from "react";

// A speedometer/gauge arc showing progress
// value / max between 0..1
function Speedometer({ value = 0, max = 100, size = 160, label, sublabel, color = "#00e5ff" }) {
  const clamped = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2 + 8;

  // 240deg arc from -210deg to 30deg (starting bottom-left, ending bottom-right)
  const startAngle = -210;
  const endAngle = 30;
  const totalArc = endAngle - startAngle; // 240

  const polar = (angle) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };

  const startPt = polar(startAngle);
  const endPt = polar(endAngle);
  const currAngle = startAngle + totalArc * clamped;
  const currPt = polar(currAngle);

  const trackPath = `M ${startPt.x} ${startPt.y} A ${radius} ${radius} 0 1 1 ${endPt.x} ${endPt.y}`;
  const fillPath = `M ${startPt.x} ${startPt.y} A ${radius} ${radius} 0 ${
    totalArc * clamped > 180 ? 1 : 0
  } 1 ${currPt.x} ${currPt.y}`;

  // Tick marks
  const ticks = [];
  for (let i = 0; i <= 10; i++) {
    const angle = startAngle + (totalArc * i) / 10;
    const inner = polar(angle);
    const outerR = radius + strokeWidth / 2 + 4;
    const outer = {
      x: cx + outerR * Math.cos((angle * Math.PI) / 180),
      y: cy + outerR * Math.sin((angle * Math.PI) / 180),
    };
    const isFilled = i / 10 <= clamped;
    ticks.push(
      <line
        key={i}
        x1={inner.x}
        y1={inner.y}
        x2={outer.x}
        y2={outer.y}
        stroke={isFilled ? color : "rgba(139, 155, 209, 0.15)"}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    );
  }

  const gradId = "gaugeGrad" + Math.random().toString(36).slice(2, 8);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity="0.6" />
            <stop offset="50%" stopColor={color} stopOpacity="1" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Track */}
        <path d={trackPath} fill="none" stroke="rgba(139, 155, 209, 0.1)" strokeWidth={strokeWidth} strokeLinecap="round" />

        {/* Ticks */}
        {ticks}

        {/* Fill */}
        {clamped > 0 && (
          <path
            d={fillPath}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 6px ${color})` }}
          />
        )}

        {/* Needle dot */}
        <circle cx={currPt.x} cy={currPt.y} r="4" fill={color} style={{ filter: `drop-shadow(0 0 5px ${color})` }} />

        {/* Center label */}
        {label && (
          <text
            x={cx}
            y={cy - 4}
            textAnchor="middle"
            fill="var(--text-primary)"
            style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700 }}
          >
            {label}
          </text>
        )}
        {sublabel && (
          <text
            x={cx}
            y={cy + 14}
            textAnchor="middle"
            fill="var(--text-muted)"
            style={{ fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase" }}
          >
            {sublabel}
          </text>
        )}
      </svg>
    </div>
  );
}

export default Speedometer;
